import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Filter, 
  Eye,
  MessageSquare,
  Tag,
  User,
  Building2,
  TrendingDown,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function CaseManagement() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [branches, setBranches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [actionTaken, setActionTaken] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchBranches();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterPriority, filterBranch]);

  const fetchBranches = async () => {
    try {
      const response = await fetch(`${API}/branches`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/cases/stats/summary`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCases = async () => {
    try {
      let url = `${API}/cases`;
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPriority && filterPriority !== 'all') params.append('priority', filterPriority);
      if (filterBranch && filterBranch !== 'all') params.append('branch_id', filterBranch);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCases(data);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCase = async () => {
    if (!selectedCase) return;
    setUpdating(true);

    try {
      const updateData = {};
      if (actionTaken) updateData.action_taken = actionTaken;
      if (newStatus) updateData.status = newStatus;

      const response = await fetch(`${API}/cases/${selectedCase.case_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('Case updated successfully');
        setDetailOpen(false);
        fetchCases();
        fetchStats();
      } else {
        toast.error('Failed to update case');
      }
    } catch (error) {
      toast.error('Failed to update case');
    } finally {
      setUpdating(false);
    }
  };

  const openCaseDetail = (caseData) => {
    setSelectedCase(caseData);
    setActionTaken(caseData.action_taken || '');
    setNewStatus(caseData.status);
    setDetailOpen(true);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-[#D62B27]/15 text-[#D62B27] border-[#D62B27]/30',
      in_progress: 'bg-[#F1D511]/20 text-[#b45309] border-[#F1D511]/30',
      closed: 'bg-[#4EBC87]/15 text-[#059669] border-[#4EBC87]/30'
    };
    const labels = { open: 'Open', in_progress: 'In Progress', closed: 'Closed' };
    return (
      <Badge variant="outline" className={styles[status] || ''}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-[#64748B]/15 text-[#64748B]',
      medium: 'bg-[#5BC4BF]/15 text-[#5BC4BF]',
      high: 'bg-[#F97316]/15 text-[#F97316]',
      critical: 'bg-[#D62B27]/15 text-[#D62B27]'
    };
    return (
      <Badge className={styles[priority] || ''}>
        {priority?.toUpperCase()}
      </Badge>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="cases-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Montserrat']" data-testid="cases-title">
              Case Management
            </h1>
            <p className="text-[#64748B] mt-1">
              Track and resolve customer issues
            </p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="border-[#E2E8F0] shadow-sm" data-testid="stat-open-cases">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D62B27]/10 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-[#D62B27]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{stats.open_cases}</p>
                    <p className="text-xs text-[#64748B]">Open Cases</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0] shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F1D511]/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#b45309]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{stats.status_breakdown.in_progress}</p>
                    <p className="text-xs text-[#64748B]">In Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0] shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#4EBC87]/15 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#4EBC87]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{stats.status_breakdown.closed}</p>
                    <p className="text-xs text-[#64748B]">Closed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0] shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#603795]/10 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-[#603795]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{stats.priority_breakdown.critical + stats.priority_breakdown.high}</p>
                    <p className="text-xs text-[#64748B]">High Priority</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Top Gaps */}
        {stats?.top_gaps?.length > 0 && (
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-[#D62B27]" />
                Top Issues
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {stats.top_gaps.map((gap, i) => (
                  <Badge key={gap.gap || i} variant="outline" className="text-sm py-1 px-3">
                    {gap.gap} <span className="ml-1 text-[#D62B27]">({gap.count})</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border-[#E2E8F0] shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2 text-sm text-[#64748B]">
                <Filter className="w-4 h-4" />
                <span>Filter:</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[140px]" data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="w-[140px]" data-testid="filter-priority">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterBranch} onValueChange={setFilterBranch}>
                  <SelectTrigger className="w-[160px]" data-testid="filter-branch">
                    <SelectValue placeholder="Branch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.branch_id} value={branch.branch_id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {(filterStatus !== 'all' || filterPriority !== 'all' || filterBranch !== 'all') && (
                  <Button variant="ghost" size="sm" onClick={() => {
                    setFilterStatus('all');
                    setFilterPriority('all');
                    setFilterBranch('all');
                  }}>
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cases List */}
        <Card className="border-[#E2E8F0] shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="loading-spinner mx-auto mb-4"></div>
                <p className="text-[#64748B]">Loading cases...</p>
              </div>
            ) : cases.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle className="w-12 h-12 text-[#4EBC87] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A] font-['Montserrat']">No cases found</h3>
                <p className="text-[#64748B] mt-1">
                  {filterStatus !== 'all' || filterPriority !== 'all' ? 'Try adjusting your filters' : 'Cases are auto-created from negative feedback'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[#E2E8F0]">
                {cases.map((caseData) => (
                  <div 
                    key={caseData.case_id}
                    className="p-4 hover:bg-[#F8F9FA] transition-colors cursor-pointer"
                    onClick={() => openCaseDetail(caseData)}
                    data-testid={`case-${caseData.case_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {getStatusBadge(caseData.status)}
                          {getPriorityBadge(caseData.priority)}
                          <span className="text-xs text-[#64748B]">
                            {formatDate(caseData.created_at)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 mb-2">
                          <Tag className="w-4 h-4 text-[#603795]" />
                          <span className="font-medium text-[#0F172A]">{caseData.gap}</span>
                        </div>
                        
                        {caseData.customer_feedback && (
                          <p className="text-sm text-[#64748B] line-clamp-2 mb-2">
                            "{caseData.customer_feedback}"
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 text-xs text-[#64748B]">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {branches.find(b => b.branch_id === caseData.branch_id)?.name || caseData.branch_id}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            Net Promoter&reg; Score: {Number(caseData.nps_score || 0).toFixed(1)}
                          </span>
                        </div>
                        
                        {caseData.ai_keywords?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {caseData.ai_keywords.slice(0, 3).map((keyword, i) => (
                              <span key={keyword} className="text-xs bg-[#1A3E6F]/10 text-[#1A3E6F] px-2 py-0.5 rounded">
                                {keyword}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <Button variant="ghost" size="sm" className="text-[#64748B]">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Case Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#D62B27]" />
                Case Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedCase && (
              <div className="space-y-6 mt-4">
                {/* Status & Priority */}
                <div className="flex items-center gap-3">
                  {getStatusBadge(selectedCase.status)}
                  {getPriorityBadge(selectedCase.priority)}
                </div>

                {/* Gap */}
                <div className="bg-[#F8F9FA] p-4 rounded-lg">
                  <p className="text-sm text-[#64748B] mb-1">Identified Gap</p>
                  <p className="font-semibold text-[#0F172A] flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[#603795]" />
                    {selectedCase.gap}
                  </p>
                  {selectedCase.department && (
                    <p className="text-sm text-[#64748B] mt-1">
                      {selectedCase.department} → {selectedCase.category}
                    </p>
                  )}
                </div>

                {/* Customer Feedback */}
                {selectedCase.customer_feedback && (
                  <div>
                    <p className="text-sm font-medium text-[#0F172A] mb-2">Customer Feedback</p>
                    <p className="text-sm text-[#64748B] bg-white p-3 rounded-lg border border-[#E2E8F0]">
                      "{selectedCase.customer_feedback}"
                    </p>
                  </div>
                )}

                {/* AI Keywords */}
                {selectedCase.ai_keywords?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-[#0F172A] mb-2">AI Detected Keywords</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedCase.ai_keywords.map((keyword, i) => (
                        <Badge key={keyword} variant="outline">{keyword}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggested Action */}
                {selectedCase.suggested_action && (
                  <div className="bg-[#1A3E6F]/5 p-4 rounded-lg border border-[#1A3E6F]/20">
                    <p className="text-sm font-medium text-[#1A3E6F] mb-1">Suggested Action</p>
                    <p className="text-sm text-[#0F172A]">{selectedCase.suggested_action}</p>
                  </div>
                )}

                {/* Customer Contact */}
                {selectedCase.customer_contact && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-[#64748B]" />
                    <span className="text-[#64748B]">Contact:</span>
                    <span className="text-[#0F172A]">{selectedCase.customer_contact}</span>
                  </div>
                )}

                {/* Action Taken */}
                <div>
                  <p className="text-sm font-medium text-[#0F172A] mb-2">Action Taken</p>
                  <Textarea
                    value={actionTaken}
                    onChange={(e) => setActionTaken(e.target.value)}
                    placeholder="Describe the action taken to resolve this case..."
                    rows={3}
                    data-testid="action-taken-input"
                  />
                </div>

                {/* Status Update */}
                <div>
                  <p className="text-sm font-medium text-[#0F172A] mb-2">Update Status</p>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setDetailOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpdateCase}
                    className="bg-[#1A3E6F] hover:bg-[#122c50]"
                    disabled={updating}
                    data-testid="update-case-btn"
                  >
                    {updating ? 'Updating...' : 'Update Case'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
